/**
 * Typed client for the streaming-transcription bridge's WebSocket protocol
 * (`backend/src/scribe/streaming/bridge.py`). Platform-agnostic: the `WebSocket` is
 * injectable (see {@link WebSocketLike}) so this runs under vitest with a fake and
 * under native platforms with a polyfill — exactly like `ScribeApiClient`'s `FetchLike`
 * seam in `../client/index.js`.
 *
 * Protocol summary (see docs/web-streaming-client-handoff.md §2, docs/specs/phase1-1.md §3
 * for the full write-up):
 * 1. Connect to `bridgeUrl` with `?token=<Entra bearer>`.
 * 2. Send `{recording_id, job_spec, resume_from?}` as the first (control) frame once the
 *    socket opens. `job_spec.mode` is `"batch"` or `"streaming"` — durable ingest runs for
 *    both; only `"streaming"` also yields live captions.
 * 3. Send binary PCM frames via `sendAudio`; receive two kinds of JSON frames back:
 *    `{"type":"ack","offset":N}` (durable-ingest progress, both modes — never surfaced via
 *    `segments()`) and `StreamingSegment` (live captions, `mode:"streaming"` only) via
 *    `segments()`.
 * 4. Send `{"type":"stop"}` to end the session; the bridge closes with 1000 once it has
 *    persisted/finalized. Any other close code is a typed {@link StreamingError} — UNLESS
 *    it's a genuinely transient, non-terminal close (e.g. `1006` abnormal closure / a
 *    dropped connection), in which case the client auto-reconnects (fresh token,
 *    `resume_from: <last acked offset>`, replaying the un-acked send buffer) rather than
 *    surfacing an error. A `1011` engine error is deliberately NOT treated as reconnectable
 *    — it means the server already marked the recording `failed`, so reconnecting would
 *    resurrect it. See `reconnecting`/`onReconnecting` below.
 *
 * NEVER log the token or the full WS URL (both may appear in `bridgeUrl`/query string), and
 * never log audio bytes.
 */
function isAckMessage(value) {
    return value.type === "ack" && typeof value.offset === "number";
}
export class StreamingError extends Error {
    name = "StreamingError";
    code;
    closeCode;
    constructor(code, message, closeCode) {
        super(message);
        this.code = code;
        if (closeCode !== undefined)
            this.closeCode = closeCode;
    }
}
/** Maps a WebSocket close code to a {@link StreamingErrorCode}; unknown non-1000 codes → "network". */
export function closeCodeToError(code) {
    switch (code) {
        case 4400:
            return "bad_request";
        case 4401:
            return "unauthorized";
        case 4402:
            return "budget_exceeded";
        case 4403:
            return "forbidden";
        case 4404:
            return "not_found";
        case 1011:
            return "engine_error";
        default:
            return "network";
    }
}
/** Close codes that are terminal — no reconnect is attempted, the session ends.
 * `4400`/`4401`/`4403`/`4404` are handshake/bind rejections (bad request, auth, permission,
 * not-found — including the bridge refusing to (re)bind a recording that's already
 * `failed`/`completed`, see `bridge.py`'s status guard) that reconnecting could only repeat
 * or resurrect. `4402` (budget_exceeded, ADMIN-4) is the same class of bind rejection — a
 * fresh reconnect from a client that keeps retrying would just get 4402 again (a
 * `resume_from` reconnect is exempt server-side and never receives this code in the first
 * place). `1011` (engine_error) means the server already flipped the recording to `failed`
 * server-side (`persist_core.handle_failed`) before closing — reconnecting would silently
 * resurrect a recording the user was told failed, so it MUST be terminal too (defense in
 * depth alongside the bridge's own status guard). Every other non-1000 code (transport
 * drops like `1006`, unknown codes, etc.) is treated as abnormal but recoverable, per
 * docs/specs/phase1-2.md §1. */
const TERMINAL_CLOSE_CODES = new Set([4400, 4401, 4402, 4403, 4404, 1011]);
/**
 * Default factory used when none is injected. Only references the global `WebSocket`
 * inside this function body (never at module top level) so the module still *imports*
 * cleanly in a non-browser (test) context that never calls it.
 */
function defaultWebSocketFactory(url) {
    return new WebSocket(url);
}
function buildUrl(bridgeUrl, token) {
    const separator = bridgeUrl.includes("?") ? "&" : "?";
    return `${bridgeUrl}${separator}token=${encodeURIComponent(token)}`;
}
function isPlainObject(value) {
    return typeof value === "object" && value !== null;
}
/** Copies audio bytes into a fresh `Uint8Array` for the replay buffer — independent of
 * whatever buffer/view the caller passed to `sendAudio` (which may be reused/mutated). */
function copyBytes(data) {
    if (ArrayBuffer.isView(data)) {
        return new Uint8Array(data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength));
    }
    return new Uint8Array(data.slice(0));
}
function defaultReconnectBackoffMs(attempt) {
    return Math.min(250 * 2 ** (attempt - 1), 4_000);
}
function delay(ms) {
    if (ms <= 0)
        return Promise.resolve();
    return new Promise((resolve) => setTimeout(resolve, ms));
}
/**
 * Opens the WS, sends the control frame, resolves once the socket is OPEN and the
 * control message has been sent. Rejects with a `StreamingError` (`unauthorized` if no
 * token; `network` if the socket errors before opening).
 */
export async function openStreamingSession(options) {
    const token = await options.getToken();
    if (!token) {
        throw new StreamingError("unauthorized", "openStreamingSession: no token available");
    }
    const factory = options.webSocketFactory ?? defaultWebSocketFactory;
    const maxReconnectAttempts = options.maxReconnectAttempts ?? 5;
    const reconnectBackoffMs = options.reconnectBackoffMs ?? defaultReconnectBackoffMs;
    const replayBufferCapBytes = options.replayBufferCapBytes ?? 4 * 1024 * 1024;
    return new Promise((resolveConnect, rejectConnect) => {
        let connectSettled = false;
        let closed = false; // terminal — no further reconnect attempts, session is over.
        let linkUp = false; // the current socket is open and usable right now.
        let currentWs;
        // Ack + replay-buffer state (docs/specs/phase1-2.md §1). `sentOffset`/`lastAckedOffset`
        // are cumulative across reconnects — only the underlying socket is swapped.
        let sentOffset = 0;
        let lastAckedOffset = options.resumeFrom ?? 0;
        let bufferedBytes = 0;
        let backpressure = false;
        const replayBuffer = [];
        // Reconnect state.
        let reconnecting = false;
        let reconnectAttempt = 0;
        let pendingStop = false;
        // SWAP-2 (docs/specs/phase3-2.md §1): current live-STT mode, mutable mid-session via
        // `setMode`. Seeded from `options.jobSpec?.mode` so the initial control frame is
        // unaffected; `buildControlFrame` always sends this (not the original jobSpec.mode) so a
        // reconnect after a toggle resumes in the right STT state.
        let currentMode = options.jobSpec?.mode ?? "streaming";
        // Queue + pending-resolver pattern for segments(): messages that arrive before a
        // consumer starts iterating are buffered; a consumer awaiting the next message when
        // none is queued registers a resolver that onmessage/onclose fulfills directly.
        const pending = [];
        let waiter = null;
        let waiterReject = null;
        let terminal = null;
        let stopDeferred = null;
        function setBackpressure(value) {
            if (value === backpressure)
                return;
            backpressure = value;
            options.onBackpressure?.(backpressure);
        }
        function handleAck(offset) {
            if (offset > lastAckedOffset)
                lastAckedOffset = offset;
            while (replayBuffer.length > 0 &&
                replayBuffer[0].offset + replayBuffer[0].bytes.length <= lastAckedOffset) {
                const dropped = replayBuffer.shift();
                bufferedBytes -= dropped.bytes.length;
            }
            setBackpressure(bufferedBytes > replayBufferCapBytes);
            options.onAck?.(lastAckedOffset);
        }
        function settleStop(code) {
            if (!stopDeferred)
                return;
            const { resolve, reject } = stopDeferred;
            stopDeferred = null;
            if (code === 1000) {
                resolve();
            }
            else {
                reject(new StreamingError(closeCodeToError(code), `Streaming session closed (${code})`, code));
            }
        }
        function setReconnecting(value) {
            if (value === reconnecting)
                return;
            reconnecting = value;
            options.onReconnecting?.(reconnecting);
        }
        function terminate(code) {
            if (terminal)
                return;
            terminal = { code };
            closed = true;
            setReconnecting(false);
            if (waiter) {
                const resolveWaiter = waiter;
                const rejectWaiter = waiterReject;
                waiter = null;
                waiterReject = null;
                if (code === 1000) {
                    resolveWaiter({ value: undefined, done: true });
                }
                else {
                    rejectWaiter(new StreamingError(closeCodeToError(code), `Streaming session closed (${code})`, code));
                }
            }
            settleStop(code);
        }
        async function* segmentsGenerator() {
            for (;;) {
                const next = pending.shift();
                if (next !== undefined) {
                    yield next;
                    continue;
                }
                if (terminal) {
                    if (terminal.code === 1000)
                        return;
                    throw new StreamingError(closeCodeToError(terminal.code), `Streaming session closed (${terminal.code})`, terminal.code);
                }
                const result = await new Promise((resolve, reject) => {
                    waiter = resolve;
                    waiterReject = reject;
                });
                if (result.done)
                    return;
                yield result.value;
            }
        }
        /** Reconnect loop: abnormal, non-terminal close → fresh socket, fresh token,
         * `resume_from: lastAckedOffset`, replay the un-acked buffer, continue. Bounded
         * retries with backoff; exhausted retries terminates with the last close code seen. */
        async function scheduleReconnect(lastCloseCode) {
            if (closed)
                return;
            if (reconnectAttempt >= maxReconnectAttempts) {
                terminate(lastCloseCode);
                return;
            }
            reconnectAttempt += 1;
            setReconnecting(true);
            await delay(reconnectBackoffMs(reconnectAttempt));
            if (closed)
                return;
            let token;
            try {
                token = await options.getToken();
            }
            catch {
                token = null;
            }
            if (closed)
                return;
            if (!token) {
                // No token available this attempt — retry again (bounded) rather than surfacing
                // an error immediately; a near-expiry token is often transiently unavailable.
                void scheduleReconnect(lastCloseCode);
                return;
            }
            let newSocket;
            try {
                newSocket = factory(buildUrl(options.bridgeUrl, token));
            }
            catch {
                void scheduleReconnect(lastCloseCode);
                return;
            }
            if (closed) {
                try {
                    newSocket.close();
                }
                catch {
                    // already gone — nothing to clean up.
                }
                return;
            }
            newSocket.binaryType = "arraybuffer";
            bindSocket(newSocket, { isReconnect: true });
            currentWs = newSocket;
        }
        function handleClose(code) {
            linkUp = false;
            if (closed)
                return;
            if (code === 1000 || TERMINAL_CLOSE_CODES.has(code)) {
                terminate(code);
                return;
            }
            void scheduleReconnect(code);
        }
        function buildControlFrame(isReconnect) {
            // SWAP-2: send the *current* mode (post-toggle), not the original options.jobSpec.mode —
            // see `currentMode` above.
            const jobSpec = { ...(options.jobSpec ?? {}), mode: currentMode };
            const frame = { recording_id: options.recordingId, job_spec: jobSpec };
            const resumeFrom = isReconnect ? lastAckedOffset : options.resumeFrom;
            if (resumeFrom !== undefined)
                frame.resume_from = resumeFrom;
            return JSON.stringify(frame);
        }
        function bindSocket(socket, opts) {
            socket.onerror = () => {
                if (!connectSettled) {
                    connectSettled = true;
                    rejectConnect(new StreamingError("network", "Streaming socket error before open"));
                }
                // Once connected (or mid-reconnect), a close event follows and drives handleClose().
            };
            socket.onopen = () => {
                socket.send(buildControlFrame(opts.isReconnect));
                linkUp = true;
                if (opts.isReconnect) {
                    // Replay exactly the un-acked tail — everything still in the buffer at the
                    // moment of reconnect, in order.
                    for (const entry of replayBuffer) {
                        socket.send(entry.bytes);
                    }
                    if (pendingStop) {
                        socket.send(JSON.stringify({ type: "stop" }));
                    }
                    reconnectAttempt = 0;
                    setReconnecting(false);
                    return;
                }
                const session = {
                    sendAudio(bytes) {
                        if (closed)
                            return;
                        const copy = copyBytes(bytes);
                        const offset = sentOffset;
                        sentOffset += copy.length;
                        replayBuffer.push({ offset, bytes: copy });
                        bufferedBytes += copy.length;
                        setBackpressure(bufferedBytes > replayBufferCapBytes);
                        if (linkUp)
                            currentWs.send(bytes);
                    },
                    segments: segmentsGenerator,
                    stop() {
                        if (closed)
                            return Promise.resolve();
                        if (linkUp) {
                            currentWs.send(JSON.stringify({ type: "stop" }));
                        }
                        else {
                            pendingStop = true;
                        }
                        return new Promise((resolve, reject) => {
                            stopDeferred = { resolve, reject };
                        });
                    },
                    close() {
                        if (closed)
                            return;
                        terminate(1000);
                        currentWs.close();
                    },
                    get ackedBytes() {
                        return lastAckedOffset;
                    },
                    get reconnecting() {
                        return reconnecting;
                    },
                    get backpressure() {
                        return backpressure;
                    },
                    get mode() {
                        return currentMode;
                    },
                    setMode(mode, reason) {
                        if (closed)
                            return;
                        currentMode = mode;
                        if (linkUp) {
                            currentWs.send(JSON.stringify(reason ? { type: "set_mode", mode, reason } : { type: "set_mode", mode }));
                        }
                        // If the link is down (reconnecting), `currentMode` is already updated above, and
                        // the next (re)connect's control frame (buildControlFrame) carries it — no separate
                        // queuing needed.
                    },
                };
                connectSettled = true;
                resolveConnect(session);
            };
            socket.onmessage = (ev) => {
                if (typeof ev.data !== "string")
                    return;
                let parsed;
                try {
                    parsed = JSON.parse(ev.data);
                }
                catch {
                    return;
                }
                if (!isPlainObject(parsed))
                    return;
                if (isAckMessage(parsed)) {
                    handleAck(parsed.offset);
                    return;
                }
                const segment = parsed;
                if (waiter) {
                    const resolveWaiter = waiter;
                    waiter = null;
                    waiterReject = null;
                    resolveWaiter({ value: segment, done: false });
                }
                else {
                    pending.push(segment);
                }
            };
            socket.onclose = (ev) => {
                handleClose(ev.code);
            };
        }
        const initialSocket = factory(buildUrl(options.bridgeUrl, token));
        initialSocket.binaryType = "arraybuffer";
        currentWs = initialSocket;
        bindSocket(initialSocket, { isReconnect: false });
    });
}
//# sourceMappingURL=client.js.map